import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis } from "recharts";
import { api, errorMessage } from "@/lib/api";
import { DataTable, Bar, num } from "@/lib/admin";
import { Notice } from "@/components/form/FormFeedback";
import { Loading } from "@/components/layout/RequireAuth";

const Kpi = ({ label, value, sub, testId }) => (
    <div className="edi-kpi p-4" data-testid={testId}>
        <p className="text-sm font-medium text-edi-muted">{label}</p>
        <p className="font-heading text-3xl font-black mt-1">{value}</p>
        {sub && <p className="text-sm text-edi-muted">{sub}</p>}
    </div>
);

const monthLabel = (m) => { const [y, mm] = m.split("-"); return `${mm}/${y}`; };

const Breakdown = ({ title, rows, testId }) => (
    <DataTable caption={title} testId={testId} rows={rows} columns={[
        { key: "value", label: "Valore" }, { key: "aziende", label: "Aziende" },
        { key: "con_assessment", label: "Con assessment" }, { key: "punteggio_medio", label: "Punteggio medio", render: (r) => num(r.punteggio_medio) },
    ]} />
);

export const Dashboard = () => {
    const { search } = useLocation();
    const [d, setD] = useState(null);
    const [error, setError] = useState("");
    useEffect(() => {
        const qs = new URLSearchParams(search);
        ["q", "page", "sort", "dir"].forEach((k) => qs.delete(k));
        setError("");
        api.get(`/admin/dashboard?${qs}`).then(({ data }) => setD(data)).catch((e) => setError(errorMessage(e)));
    }, [search]);
    if (error) return <Notice kind="error" role="alert">{error}</Notice>;
    if (!d) return <Loading />;
    const k = d.kpi;
    return (
        <div className="space-y-6" data-testid="dashboard">
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
                <Kpi label="Aziende registrate" value={k.aziende_registrate} testId="kpi-registrate" />
                <Kpi label="Aziende con assessment" value={k.aziende_con_assessment} sub={`${num(k.pct_con_assessment)}% delle registrate`} testId="kpi-con-assessment" />
                <Kpi label="Punteggio medio" value={num(k.punteggio_medio)} testId="kpi-punteggio-medio" />
                <Kpi label="Assessment compilati" value={k.assessment_compilati} testId="kpi-compilati" />
            </div>
            {d.empty ? (
                <Notice kind="info" testId="dashboard-empty">Nessun assessment compilato per i filtri selezionati</Notice>
            ) : (
                <>
                    <div className="grid gap-6 lg:grid-cols-2 items-start" data-testid="dash-levels-areas">
                        <DataTable caption="Distribuzione per livello" testId="dash-levels" rows={d.levels} columns={[
                            { key: "level", label: "Livello" }, { key: "count", label: "Aziende" },
                            { key: "pct", label: "%", render: (r) => <span className="flex items-center gap-2"><Bar value={r.pct} />{num(r.pct)}%</span> },
                        ]} />
                        <DataTable caption="Punteggio medio per area" testId="dash-areas" rows={d.areas} columns={[
                            { key: "label", label: "Area" },
                            { key: "media", label: "Media", render: (r) => <span className="flex items-center gap-2"><Bar value={r.media} />{num(r.media)}</span> },
                        ]} />
                    </div>
                    <section className="edi-card p-4" aria-labelledby="trend-title" data-testid="dash-trend">
                        <h2 id="trend-title" className="font-heading font-bold text-base">Andamento mensile</h2>
                        <div className="h-56 mt-2 text-edi-text" role="img" aria-label="Grafico dell'andamento mensile: i dati sono nella tabella seguente">
                                <LineChart responsive style={{ width: "100%", height: "100%" }} data={d.trend.map((t) => ({ ...t, m: monthLabel(t.month) }))} margin={{ left: -16, right: 12, top: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#9aa2a5" />
                                    <XAxis dataKey="m" tick={{ fill: "currentColor" }} /><YAxis domain={[0, 100]} tick={{ fill: "currentColor" }} /><Tooltip />
                                    <Line dataKey="punteggio_medio" name="Punteggio medio" stroke="currentColor" strokeWidth={2} dot={{ r: 4, fill: "#BCCF00", stroke: "currentColor", strokeWidth: 1.5 }} isAnimationActive={false} />
                                </LineChart>
                        </div>
                        <div className="overflow-x-auto"><table className="w-full text-sm text-left mt-2">
                            <caption className="sr-only">Andamento mensile: assessment compilati e punteggio medio</caption>
                            <thead><tr className="border-b border-edi-border-strong"><th scope="col" className="py-1 pr-4">Mese</th><th scope="col" className="py-1 pr-4">Assessment compilati</th><th scope="col">Punteggio medio</th></tr></thead>
                            <tbody>{d.trend.map((t) => <tr key={t.month} className="border-b border-edi-border"><td className="py-1 pr-4">{monthLabel(t.month)}</td><td className="pr-4">{t.count}</td><td>{num(t.punteggio_medio)}</td></tr>)}</tbody>
                        </table></div>
                    </section>
                    <div className="grid gap-6">
                        <Breakdown title="Per settore" rows={d.breakdowns.settore} testId="dash-settore" />
                        <Breakdown title="Per fascia dipendenti" rows={d.breakdowns.fascia_dipendenti} testId="dash-dipendenti" />
                        <Breakdown title="Per fascia di fatturato" rows={d.breakdowns.fascia_fatturato} testId="dash-fatturato" />
                    </div>
                    <div className="grid gap-6 xl:grid-cols-2">
                        <DataTable caption="Obiettivi più indicati" testId="dash-goals" rows={d.goals} columns={[{ key: "label", label: "Obiettivo" }, { key: "count", label: "Risposte" }]} />
                        <DataTable caption="Ostacoli più indicati" testId="dash-obstacles" rows={d.obstacles} columns={[{ key: "label", label: "Ostacolo" }, { key: "count", label: "Risposte" }]} />
                    </div>
                    <section aria-labelledby="questions-title">
                        <h2 id="questions-title" className="font-heading font-bold text-xl mb-3">Distribuzione delle risposte (questionario versione {d.questionnaire_version})</h2>
                        <div className="space-y-2">
                            {d.questions.map((q) => (
                                <details key={q.id} className="edi-card p-3" data-testid={`dash-question-${q.id}`}>
                                    <summary className="cursor-pointer font-semibold">Step {q.step} · {q.text} <span className="text-edi-muted font-normal">({q.answered} risposte)</span></summary>
                                    {q.options ? (
                                        <div className="overflow-x-auto"><table className="w-full text-sm text-left mt-2">
                                            <caption className="sr-only">Distribuzione risposte: {q.text}</caption>
                                            <thead><tr><th scope="col" className="py-1 pr-4">Risposta</th><th scope="col" className="pr-4">Conteggio</th><th scope="col">%</th></tr></thead>
                                            <tbody>{q.options.map((o) => <tr key={o.label} className="border-t border-edi-border"><td className="py-1 pr-4">{o.label}</td><td className="pr-4">{o.count}</td><td><span className="flex items-center gap-2"><Bar value={o.pct} />{num(o.pct)}%</span></td></tr>)}</tbody>
                                        </table></div>
                                    ) : (
                                        <ul className="list-disc pl-6 mt-2">{q.texts.length ? q.texts.map((t, i) => <li key={i}>{t}</li>) : <li>Nessuna risposta</li>}</ul>
                                    )}
                                </details>
                            ))}
                        </div>
                    </section>
                </>
            )}
        </div>
    );
};
